<template>
  <div class="job-board-point-dashboard-container">
    <div class="header-container">
      <span class="name-label">{{ $t(`${i18nPrefix}.title.index`) }}</span>
      <el-select
        class="channel-select"
        v-model="channelType"
        @change="handelChangeChannel"
       >
        <el-option
          v-for="item in channelList"
          :key="item.key"
          :label="item.i18nName"
          :value="item.key">
        </el-option>
      </el-select>
    </div>
    <TableList
      v-loading="showLoading"
      class="table-container"
      :listData="listData"
      :defaultExpandAll="false"
      :tableColumns="tableColumns"
      no-operation
    >
      <template #btn-group>
        <div></div>
      </template>
      <template #points="{ row }">
        <div class="points-container">
          <span class="points" :class="row.is_root && 'root'">{{ row.points | formatBigNumber }}</span>
          <i v-if="!row.is_root" class="transfer-icon iconfont iconEditor1" @click="handelTransferDialog(row)"/>
          <i class="history-icon iconfont iconHistory1" @click="handelToHistory(row)"/>
        </div>
      </template>
      <template #count="{ row }">
        <div class="count-container">
          <span class="count-label">{{ (row.max_count || channelDefaultCountForLinkedin) | formatBigNumber }}</span>
          <i class="iconfont iconEditor1" @click="handelMaxCountDialog(row)"/>
        </div>
      </template>
    </TableList>
    <DialogWrapper
      closeOnCancel
      destroyOnClose
      :show.sync="transferDialog.show"
      :closeOnClickModal="false"
      @save="handelTransferPoints"
      >
      <div class="interior-points-transfer">
        <span class="title">{{ $t(`${i18nPrefix}.label.surplusPointsTitle`, { companyName: transferDialog.title}) + ' ' + formatBigNumber(transferDialog.points) }}</span>
        <div class="operate-container">
          <div class="type-select">
            <span :class="transferDialog.type === '+' && 'select'" @click="transferDialog.type = '+'"><i class="el-icon-plus"/></span>
            <span :class="transferDialog.type === '-' && 'select'" @click="transferDialog.type = '-'"><i class="el-icon-minus"/></span>
          </div>
          <div class="info-container">
            <el-input
              class="input number-input points-input"
              :value="transferDialog.operatePoints"
              v-model.number="transferDialog.operatePoints"
              :placeholder="$t(`${i18nPrefix}.placeholder.pointsInput`)"
              @input="handelOperatePointsChange"
              >
            </el-input>
            <el-input class="input note-input" :placeholder="$t(`${i18nPrefix}.placeholder.notes`)" v-model="transferDialog.note"></el-input>
            <span class="tips" v-show="transferDialog.showTips">{{ $t(`${i18nPrefix}.message.pointsRequired`) }}</span>
          </div>
        </div>
      </div>
      <Loading class="loading" v-show="transferDialog.loading"/>
    </DialogWrapper>
    <DialogWrapper
      :show.sync="maxCountDialog.show"
      :closeOnCancel="true"
      :closeOnClickModal="false"
      :destroyOnClose="true"
      @save="saveMaxCount"
      >
      <div class="max-count-container">
        <span class="title">{{ maxCountDialog.title }}</span>
        <div class="operate-container">
          <span class="max-count-label">{{ $t(`${i18nPrefix}.label.purchasedJobsCount`) }}</span>
          <el-input class="number-input" type="number" v-model="maxCountDialog.count"></el-input>
        </div>
        <span class="tips" v-show="maxCountDialog.showTips">{{ $t(`${i18nPrefix}.message.maxCountGatherZero`) }}</span>
      </div>
      <Loading class="loading" v-show="maxCountDialog.loading"/>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import { addI18nProperty } from '@/i18n'
import { mapActions } from 'vuex'
import { formatBigNumber } from '@/components/filters'
import TableList from '../../components/jobBoardPoint/table.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import Loading from '@/components/loading/rectLoading.vue'
import { channelDefaultCountForLinkedin } from '@/constant/company'

const defaultChannel = 'ajinga'
const pointChannel = '51job'
const countChannel = 'linkedin'

export default {
  components: {
    TableList,
    DialogWrapper,
    Loading
  },
  data () {
    return {
      formatBigNumber,
      channelDefaultCountForLinkedin,
      i18nPrefix: 'admin.company.jobBoardPointDashboard',
      listData: [],
      channelType: '',
      channelList: [],
      showLoading: false,
      transferDialog: {
        show: false,
        loading: false,
        title: '',
        points: '',
        companyId: '',
        operatePoints: '',
        note: '',
        type: '+',
        showTips: false
      },
      maxCountDialog: {
        show: false,
        loading: false,
        title: '',
        count: '',
        companyId: '',
        showTips: false
      }
    }
  },
  computed: {
    isEn () {
      return this.$i18n.locale === 'en'
    },
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.companyName`),
          dataKey: this.isEn ? 'name' : 'cn_name'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.companyID`),
          dataKey: 'id'
        }
      ]
      if (!this.channelType || this.channelType === pointChannel) {
        columns.push({
          label: this.$t(`${this.i18nPrefix}.label.allocatePoints`),
          dataKey: 'points',
          slotName: 'points'
        })
      }
      columns.push({
        label: this.$t(`${this.i18nPrefix}.label.${this.channelType === countChannel ? 'activeJobs' : 'postingJobs'}`),
        dataKey: 'posting_count',
        formatter: (val) => this.formatBigNumber(val)
      })
      if (this.channelType === countChannel) {
        columns.push({
          label: this.$t(`${this.i18nPrefix}.label.purchasedJobsCount`),
          dataKey: 'count',
          slotName: 'count'
        })
      }
      return columns
    }
  },
  async created () {
    await this.initJobChannel()
    await this.initListData()
  },
  mounted () {
  },
  methods: {
    ...mapActions('company', ['getCompanyJobPostingChannelList', 'getCompanyJobBoardPointDashboardList', 'transferJobPostingPoints', 'changeJobPostingMaxCount']),
    async initListData () {
      this.showLoading = true
      try {
        const resp = await this.getCompanyJobBoardPointDashboardList({ channel_key: this.channelType || defaultChannel })
        if (resp) {
          this.listData = resp
        }
      } finally {
        this.showLoading = false
      }
    },
    async initJobChannel () {
      const channelType = this.$route.query.channelType
      await this.getCompanyJobPostingChannelList().then(resp => {
        if (resp && resp.post_channels) {
          this.channelList = _.map(resp.post_channels, channel => {
            addI18nProperty(channel, 'i18nName', 'cn_name', 'name')
            return channel
          })
          this.channelType = defaultChannel
          if (channelType && _.find(this.channelList, 'key', channelType)) {
            this.channelType = channelType
          }
        }
      })
    },
    handelTransferDialog (row) {
      this.transferDialog.title = row[this.isEn ? 'name' : 'cn_name']
      this.transferDialog.points = row.points
      this.transferDialog.companyId = row.id
      this.transferDialog.operatePoints = ''
      this.transferDialog.note = ''
      this.transferDialog.type = '+'
      this.transferDialog.showTips = false
      this.transferDialog.show = true
    },
    handelChangeChannel () {
      this.$router.push({ path: this.$router.path, query: { ...this.$route.query, channelType: this.channelType } })
      this.initListData()
    },
    handelOperatePointsChange (value) {
      const number = value?.replace(/[^0-9]/g, '')
      this.transferDialog.operatePoints = number && formatBigNumber(_.replace(number.toString(), /,/g, ''))
    },
    handelToHistory (row) {
      this.$router.push({ name: 'companyAdminJobBoardPointDashboardHistory', params: { companyId: row.id }, query: this.$route.query })
    },
    async handelTransferPoints () {
      const transferDialog = this.transferDialog
      if (this.channelType !== pointChannel) {
        transferDialog.show = false
        return
      }
      transferDialog.showTips = false
      transferDialog.loading = true
      if (!transferDialog.operatePoints || transferDialog.operatePoints <= '0') {
        transferDialog.showTips = true
        transferDialog.loading = false
        return
      }
      const params = {
        points: _.chain(transferDialog.operatePoints).replace(/[,']/g, '').toNumber().value(),
        note: transferDialog.note,
        action: transferDialog.type === '+' ? 'add' : 'subtract',
        channel_key: this.channelType,
        target_company_id: transferDialog.companyId
      }
      try {
        const resp = await this.transferJobPostingPoints(params)
        if (resp && resp.message) {
          if (resp.message === 'success') {
            this.initListData()
            transferDialog.show = false
          } else {
            this.$message.error('error')
          }
        }
      } finally {
        transferDialog.loading = false
        transferDialog.showTips = false
      }
    },
    handelMaxCountDialog (row) {
      this.maxCountDialog.title = row[this.isEn ? 'name' : 'cn_name']
      this.maxCountDialog.companyId = row.id
      this.maxCountDialog.count = row.max_count || channelDefaultCountForLinkedin
      this.maxCountDialog.showTips = false
      this.maxCountDialog.show = true
    },
    async saveMaxCount () {
      const maxCountDialog = this.maxCountDialog
      if (this.channelType !== countChannel) {
        maxCountDialog.show = false
        return
      }
      maxCountDialog.showTips = false
      maxCountDialog.loading = true
      if (maxCountDialog.count < 0) {
        maxCountDialog.showTips = true
        maxCountDialog.loading = false
        return
      }
      const params = {
        channel_key: this.channelType,
        company_id: maxCountDialog.companyId,
        count: maxCountDialog.count
      }
      try {
        const resp = await this.changeJobPostingMaxCount(params)
        if (resp && resp.message) {
          if (resp.message === 'success') {
            this.initListData()
            maxCountDialog.show = false
          } else {
            this.$message.error('error')
          }
        }
      } finally {
        maxCountDialog.loading = false
        maxCountDialog.showTips = false
      }
    }
  },
  watch: {
  }
}
</script>

<style lang="scss" scoped>
.job-board-point-dashboard-container {
  background: #FFFFFF;
  padding: 30px;

  .header-container {
    margin-bottom: 20px;
    padding: 10px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .name-label {
      width: 240px;
      font-size: 16px;
    }
    .channel-select {
      width: 400px;
    }
  }
  .table-container {
    .points-container {
      display: flex;
      align-items: center;
      .points {
        display: inline-flex;
        min-width: 45px;
        line-height: 1;

        &.root {
          margin-right: 17px;
        }
      }

      .history-icon {
        margin-left: 5px;
      }
    }

    .iconfont {
      cursor: pointer;
      color: #0096CC;
      font-size: 18px;
      line-height: 1;
    }

    .count-container {
      display: flex;
      align-items: center;

      .count-label {
        margin-right: 10px;
        font-size: 14px;
        line-height: 1;
      }

    }
  }
  .interior-points-transfer {

    .title {
      font-size: 22px;
      color: #303133;
    }

    .operate-container {
      margin-top: 30px;
      text-align: center;
      display: flex;
      justify-content: flex-start;

      .type-select {
        display: flex;
        color: #606266;
        margin-right: 20px;

        > span {
          width: 32px;
          height: 32px;
          font-size: 18px;
          line-height: 32px;
          border: 1px solid #E4E7ED ;
          border-radius: 2px;
          cursor: pointer;

          > i {
            font-weight: bold;
          }

          &:first-child {
            margin: 0;
            border-right: 0;
          }
        }

        .select {
          color: #0096cc;
        }
      }
    }

    .info-container {
      text-align: left;
      .input {
        margin-bottom: 15px;
      }

      .tips {
        color: #ff0000;
      }
    }
  }
  .max-count-container {
    padding-bottom: 20px;

    .title {
      font-size: 22px;
      color: #303133;
    }

    .operate-container {
      margin-top: 30px;
      display: flex;
      align-items: center;
      font-size: 16px;

      .max-count-label {
        word-break: keep-all;
        white-space: nowrap;
      }

      .number-input {
        margin-left: 20px;
      }
    }

    .tips {
      margin-left: 35%;
      color: #ff0000;
    }
  }
  .loading {
    position: absolute;
  }
  ::v-deep {
    .table-header {
      display: none;
    }
    .number-input {
      ::-webkit-inner-spin-button,
      ::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
    .el-select {
      &.channel-select {
        .el-input__inner {
          color: #909399;
          border-radius: 2px;
        }
      }
    }
    .el-table {
      border-color: #DFDFDF;
      &::after {
        background-color: #DFDFDF;
      }
      &::before {
        background-color: #DFDFDF;
      }
      .el-table__header-wrapper {
        .el-table__cell {
          background: #FAFAFA;
          border-color: #DFDFDF;
        }
      }
      .el-table__body {
        border-color: #DFDFDF;
        .el-table__cell {
          border-color: #DFDFDF;
        }
      }
    }
    .el-dialog {
      width: 40%;
    }
  }
}
</style>
